<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import { Form, Input, InputNumber, Select, SelectOption, Checkbox, Button } from 'ant-design-vue'
  import { AdvancedAnalysisSchema } from '../models/AdvancedAnalysisSchema'
  import { AdvancedAnalysisField } from '../models/AdvancedAnalysisField'
  import { AdvancedAnalysisFieldType } from '../models/AdvancedAnalysisFieldType'
  import { AdvancedAnalysisService } from '../services/AdvancedAnalysisService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import SelectOrCreateAdvancedAnalysisConfiguration from './SelectOrCreateAdvancedAnalysisConfiguration.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import AdvancedAnalysisHelpPopover from '../help/components/AdvancedAnalysisHelpPopover.vue'
  import ArrayAdvancedAnalysisBuilder from './ArrayAdvancedAnalysisBuilder.vue'
  import { AdvancedAnalysis } from '../models/AdvancedAnalysis'
  import { AdvancedAnalysisUiService } from '../services/AdvancedAnalysisUiService'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AdvancedAnalysisJsonConfigurationService } from '../services/AdvancedAnalysisJsonConfigurationService'
import JsonAdvancedAnalysisConfiguration from './JsonAdvancedAnalysisConfiguration.vue'

  interface IObjectAdvancedAnalysisBuilderProps {
    configSchemas: Array<AdvancedAnalysisSchema>
    advancedAnalysisField: AdvancedAnalysisField
    rootAdvancedAnalysis: AdvancedAnalysis | null
    modelValue: Record<string, any>
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IObjectAdvancedAnalysisBuilderEmits {
    (e: 'update:modelValue', value: Record<string, any>): void
  }

  const props = defineProps<IObjectAdvancedAnalysisBuilderProps>()
  const emit = defineEmits<IObjectAdvancedAnalysisBuilderEmits>()

  const formRef = ref()
  const modelValue = reactive<Record<string, any>>({ ...(props.modelValue ?? {}) })
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.advancedAnalysisField.field_type !== AdvancedAnalysisFieldType.Object) return
    // Support nested object structures
    if (
      (props.advancedAnalysisField.item_schema == null &&
        props.advancedAnalysisField.fields == null) ||
      (props.advancedAnalysisField.item_schema != null &&
        props.advancedAnalysisField.item_schema.field_type !== AdvancedAnalysisFieldType.Object)
    ) {
      throw new Error(
        'ObjectAdvancedAnalysisBuilder requires an Object type with fields or child item_schema of type Object'
      )
    }

    if (props.advancedAnalysisField.fields != null) {
      configuration.value = AdvancedAnalysisService.initializeAdvancedAnalysisFieldConfiguration(
        props.advancedAnalysisField.fields!
      )
    } else if (props.advancedAnalysisField.item_schema != null) {
      configuration.value = AdvancedAnalysisService.initializeAdvancedAnalysisConfigurationForField(
        props.advancedAnalysisField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.advancedAnalysisField) {
      emit('update:modelValue', configuration.value)
    }
  }

  // placeholder for future type-change handling
  // @ts-expect-error
  const handleConfigTypeChanged = (cfgType: string, value: {}) => {
    // Intentionally left as no-op: type conversion for nested object config not currently supported
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()
  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Watch for model changes
  watch(
    () => props.modelValue,
    (newVal) => {
      // Sync reactive modelValue into configuration
      Object.assign(modelValue, newVal ?? {})
      configuration.value = { ...(newVal ?? {}) }
    },
    { immediate: true }
  )

  watch(
    () => props.advancedAnalysisField,
    (newField) => {
      if (newField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props?.advancedAnalysisField?.fields
      ? props.advancedAnalysisField.fields.filter(
          (f: any) =>
            f.field_type !== AdvancedAnalysisFieldType.Object &&
            f.field_type !== AdvancedAnalysisFieldType.Array &&
            f.field_type !== AdvancedAnalysisFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.advancedAnalysisField?.fields?.findIndex(
        (x: AdvancedAnalysisField) =>
          x.field_type === AdvancedAnalysisFieldType.Array ||
          x.field_type === AdvancedAnalysisFieldType.Object ||
          x.field_type === AdvancedAnalysisFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: AdvancedAnalysisField): boolean => {
    return (
      field.field_type !== AdvancedAnalysisFieldType.Object &&
      field.field_type !== AdvancedAnalysisFieldType.Json
    )
  }

  const isObjectWithNestedObject = computed(
    () =>
      props.advancedAnalysisField.fields == null && props.advancedAnalysisField.item_schema != null
  )

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.advancedAnalysisField?.display_field as
      | string
      | string[]
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.advancedAnalysisField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-advancedanalysis-builder-grid-span">
    <ObjectAdvancedAnalysisBuilder
      :configuration-level="props.configurationLevel"
      :root-advanced-analysis="props.rootAdvancedAnalysis"
      v-model="configuration[props.advancedAnalysisField.item_schema!.name]"
      :advanced-analysis-field="props.advancedAnalysisField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      :config-schemas="configSchemas"
      :is-condensed-layout="props.isCondensedLayout"
    />
  </div>

  <div v-else>
    <div
      v-if="advancedAnalysisField"
      :class="[
        'border',
        'advanced-analysis-configuration',
        'h-full',
        'w-full',
        {
          'child-config-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <!-- Header -->
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ advancedAnalysisField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  size="large"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <ExpandOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <CompressOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <!-- Configuration Form -->
        <div
          :class="[
            'flex-1',
            'overflow-y-auto',
            { 'collapsed-advancedanalysis-configuration': isCollapsed }
          ]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div
              :class="{
                'object-advancedanalysis-fields-grid': true,
                expanded: !props.isCondensedLayout
              }"
            >
              <div
                v-for="field in advancedAnalysisField.fields"
                :key="field.name"
                class="object-advancedanalysis-form-field-grid"
                :class="{
                  'array-advancedanalysis-builder-grid-span':
                    field.field_type === AdvancedAnalysisFieldType.Array,
                  'object-advancedanalysis-builder-grid-span':
                    field.field_type === AdvancedAnalysisFieldType.Object,
                  'json-advancedanalysis-configuration-grid-span':
                    field.field_type === AdvancedAnalysisFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== AdvancedAnalysisFieldType.Object &&
                    field.field_type !== AdvancedAnalysisFieldType.Array &&
                    field.field_type !== AdvancedAnalysisFieldType.Json,
                  '!hidden': !AdvancedAnalysisUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootAdvancedAnalysis,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="table-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="table-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>

                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <AdvancedAnalysisHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === AdvancedAnalysisFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateAdvancedAnalysisConfiguration
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.MultiSelect"
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === AdvancedAnalysisFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Array Type -->
                <ArrayAdvancedAnalysisBuilder
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Array"
                  :root-advanced-analysis="props.rootAdvancedAnalysis"
                  :configuration-level="props.configurationLevel"
                  v-model="configuration[field.name]"
                  :advanced-analysis-field="field"
                  :key="advancedAnalysisField?.name"
                  @update:modelValue="handleConfigurationChange"
                  @child-config-type-changed="handleConfigTypeChanged"
                  :config-schemas="configSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- Object Type -->
                <ObjectAdvancedAnalysisBuilder
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Object"
                  :root-advanced-analysis="props.rootAdvancedAnalysis"
                  :configuration-level="props.configurationLevel + 1"
                  v-model="configuration[field.name]"
                  :advanced-analysis-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :config-schemas="configSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- Json Type (Custom Config) -->
                <JsonAdvancedAnalysisConfiguration
                  v-else-if="field.field_type === AdvancedAnalysisFieldType.Json"
                  :advanced-analysis-schemas="props.configSchemas"
                  v-model="configuration"
                  :editor-schema="
                    AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForAdvancedAnalysis()
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @analysis-config-type-changed="handleConfigTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonAdvancedAnalysisConfiguration
                  v-else
                  :advanced-analysis-schemas="props.configSchemas"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    AdvancedAnalysisJsonConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-advancedanalysis-configuration {
    display: none;
  }

  .advanced-analysis-configuration {
    border: 1px solid #0000002e;
    padding: 10px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .object-advancedanalysis-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-advancedanalysis-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-advancedanalysis-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-advancedanalysis-builder-grid-span,
  .object-advancedanalysis-builder-grid-span,
  .json-advancedanalysis-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .table-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }
</style>
